(() => {
  const ELEMENT_NAME = 'ohs-product-carousel';
  const SOURCE_SELECTOR = '[data-ohs-carousel-source]';
  const CLONE_SELECTOR = '[data-ohs-carousel-clone]';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    '[tabindex]'
  ].join(',');

  if (customElements.get(ELEMENT_NAME)) return;

  class OHSProductCarousel extends HTMLElement {
    constructor() {
      super();

      this.viewport = null;
      this.track = null;
      this.sourceGroup = null;

      this.animationFrame = null;
      this.resumeTimer = null;
      this.resizeTimer = null;

      this.lastTimestamp = null;
      this.loopWidth = 0;
      this.offset = 0;

      this.autoplay = true;
      this.desktopSpeed = 25;
      this.mobileSpeed = 40;
      this.direction = 'left';
      this.pauseOnHover = true;
      this.resumeDelay = 700;

      this.isPaused = false;
      this.isDragging = false;
      this.hasDragged = false;
      this.isEditorSelected = false;

      this.pointerId = null;
      this.dragStartX = 0;
      this.dragStartY = 0;
      this.dragStartOffset = 0;

      this.reducedMotionQuery = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

      this.mobileQuery = window.matchMedia(
        '(max-width: 749px)'
      );

      this.animate = this.animate.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);

      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handlePointerCancel = this.handlePointerCancel.bind(this);
      this.handleClick = this.handleClick.bind(this);

      this.handleResize = this.handleResize.bind(this);
      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);
      this.handleReducedMotionChange =
        this.handleReducedMotionChange.bind(this);

      this.handleSectionSelect =
        this.handleSectionSelect.bind(this);
      this.handleSectionDeselect =
        this.handleSectionDeselect.bind(this);
    }

    connectedCallback() {
      if (this.dataset.ohsInitialised === 'true') return;

      this.viewport = this.querySelector(
        '.ohs-new-arrivals__viewport'
      );

      this.track = this.querySelector(
        '.ohs-new-arrivals__track'
      );

      this.sourceGroup = this.querySelector(
        SOURCE_SELECTOR
      );

      if (!this.viewport || !this.track || !this.sourceGroup) {
        return;
      }

      if (!this.sourceGroup.children.length) {
        return;
      }

      this.dataset.ohsInitialised = 'true';

      this.readSettings();
      this.addEventListeners();

      requestAnimationFrame(() => {
        this.rebuildLoop();
        this.setInitialOffset();
        this.render();

        if (this.shouldAnimate()) {
          this.start();
        }
      });
    }

    disconnectedCallback() {
      this.destroy();
    }

    readSettings() {
      this.autoplay = this.dataset.autoplay !== 'false';

      this.desktopSpeed = Math.max(
        1,
        Number.parseFloat(this.dataset.desktopSpeed) || 25
      );

      this.mobileSpeed = Math.max(
        1,
        Number.parseFloat(this.dataset.mobileSpeed) || 40
      );

      this.direction =
        this.dataset.direction === 'right'
          ? 'right'
          : 'left';

      this.pauseOnHover =
        this.dataset.pauseHover !== 'false';

      this.resumeDelay = Math.max(
        0,
        Number.parseInt(this.dataset.resumeDelay, 10) || 700
      );
    }

    get currentSpeed() {
      return this.mobileQuery.matches
        ? this.mobileSpeed
        : this.desktopSpeed;
    }

    shouldAnimate() {
      return (
        this.autoplay &&
        !this.reducedMotionQuery.matches &&
        !this.isEditorSelected &&
        !document.hidden
      );
    }

    rebuildLoop() {
      const oldLoopWidth = this.loopWidth;
      const oldProgress =
        oldLoopWidth > 0
          ? this.normaliseOffset(this.offset, oldLoopWidth) /
            oldLoopWidth
          : 0;

      this.removeClones();

      const trackStyles = window.getComputedStyle(this.track);

      const groupGap =
        Number.parseFloat(trackStyles.columnGap) ||
        Number.parseFloat(trackStyles.gap) ||
        0;

      const sourceWidth =
        this.sourceGroup.getBoundingClientRect().width;

      this.loopWidth = sourceWidth + groupGap;

      if (this.loopWidth <= 0) return;

      const viewportWidth =
        this.viewport.getBoundingClientRect().width;

      const requiredWidth =
        viewportWidth + this.loopWidth * 2;

      const cloneCount = Math.max(
        2,
        Math.ceil(requiredWidth / this.loopWidth) - 1
      );

      for (let index = 0; index < cloneCount; index += 1) {
        const clone = this.createClone();
        this.track.appendChild(clone);
      }

      if (oldLoopWidth > 0) {
        this.offset = oldProgress * this.loopWidth;
      }

      this.offset = this.normaliseOffset(
        this.offset,
        this.loopWidth
      );

      this.render();
    }

    createClone() {
      const clone = this.sourceGroup.cloneNode(true);

      clone.removeAttribute('data-ohs-carousel-source');
      clone.setAttribute('data-ohs-carousel-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');

      clone.querySelectorAll('[id]').forEach((element) => {
        element.removeAttribute('id');
      });

      clone
        .querySelectorAll(FOCUSABLE_SELECTOR)
        .forEach((element) => {
          element.setAttribute('tabindex', '-1');
          element.setAttribute('aria-hidden', 'true');

          if (
            element.matches(
              'button, input, select, textarea'
            )
          ) {
            element.setAttribute('disabled', '');
          }
        });

      return clone;
    }

    removeClones() {
      this.track
        .querySelectorAll(CLONE_SELECTOR)
        .forEach((clone) => clone.remove());
    }

    setInitialOffset() {
      if (this.loopWidth <= 0) return;

      this.offset =
        this.direction === 'right'
          ? this.loopWidth
          : 0;

      this.offset = this.normaliseOffset(
        this.offset,
        this.loopWidth
      );
    }

    normaliseOffset(value, width = this.loopWidth) {
      if (width <= 0) return 0;

      return ((value % width) + width) % width;
    }

    render() {
      if (!this.track) return;

      if (this.reducedMotionQuery.matches) {
        this.track.style.transform = '';
        return;
      }

      this.track.style.transform =
        `translate3d(${-this.offset}px, 0, 0)`;
    }

    start() {
      if (!this.shouldAnimate()) return;
      if (this.animationFrame) return;

      this.isPaused = false;
      this.lastTimestamp = null;

      this.animationFrame =
        window.requestAnimationFrame(this.animate);
    }

    stop() {
      if (this.animationFrame) {
        window.cancelAnimationFrame(
          this.animationFrame
        );
      }

      this.animationFrame = null;
      this.lastTimestamp = null;
    }

    pause() {
      this.isPaused = true;
      this.lastTimestamp = null;
    }

    resume() {
      if (!this.shouldAnimate()) return;

      this.isPaused = false;
      this.lastTimestamp = null;

      if (!this.animationFrame) {
        this.start();
      }
    }

    resumeAfterDelay() {
      if (this.resumeTimer) {
        window.clearTimeout(this.resumeTimer);
      }

      this.resumeTimer = window.setTimeout(() => {
        this.resume();
      }, this.resumeDelay);
    }

    animate(timestamp) {
      if (!this.isConnected || !this.track) {
        this.stop();
        return;
      }

      if (!this.shouldAnimate()) {
        this.stop();
        return;
      }

      if (this.lastTimestamp === null) {
        this.lastTimestamp = timestamp;
      }

      const elapsedSeconds = Math.min(
        (timestamp - this.lastTimestamp) / 1000,
        0.05
      );

      this.lastTimestamp = timestamp;

      if (
        !this.isPaused &&
        !this.isDragging &&
        this.loopWidth > 0
      ) {
        const movement =
          this.currentSpeed * elapsedSeconds;

        if (this.direction === 'right') {
          this.offset -= movement;
        } else {
          this.offset += movement;
        }

        this.offset = this.normaliseOffset(
          this.offset
        );

        this.render();
      }

      this.animationFrame =
        window.requestAnimationFrame(this.animate);
    }

    handlePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      if (this.reducedMotionQuery.matches) {
        return;
      }

      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartOffset = this.offset;

      this.isDragging = false;
      this.hasDragged = false;

      this.pause();
    }

    handlePointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const distanceX =
        event.clientX - this.dragStartX;

      const distanceY =
        event.clientY - this.dragStartY;

      if (!this.isDragging) {
        const horizontalDistance =
          Math.abs(distanceX);

        const verticalDistance =
          Math.abs(distanceY);

        if (
          horizontalDistance < 6 &&
          verticalDistance < 6
        ) {
          return;
        }

        if (verticalDistance > horizontalDistance) {
          this.pointerId = null;
          this.resumeAfterDelay();
          return;
        }

        this.isDragging = true;
        this.hasDragged = true;

        this.viewport.classList.add('is-dragging');

        try {
          this.viewport.setPointerCapture(
            event.pointerId
          );
        } catch (error) {
          // Pointer capture is not supported in every browser.
        }
      }

      if (!this.isDragging) return;

      event.preventDefault();

      this.offset = this.normaliseOffset(
        this.dragStartOffset - distanceX
      );

      this.render();
    }

    handlePointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      if (
        this.viewport.hasPointerCapture &&
        this.viewport.hasPointerCapture(event.pointerId)
      ) {
        try {
          this.viewport.releasePointerCapture(
            event.pointerId
          );
        } catch (error) {
          // Ignore unsupported pointer release errors.
        }
      }

      this.pointerId = null;
      this.isDragging = false;

      this.viewport.classList.remove('is-dragging');

      this.offset = this.normaliseOffset(
        this.offset
      );

      this.render();
      this.resumeAfterDelay();
    }

    handlePointerCancel(event) {
      if (
        this.pointerId !== null &&
        event.pointerId !== this.pointerId
      ) {
        return;
      }

      this.pointerId = null;
      this.isDragging = false;

      this.viewport.classList.remove('is-dragging');

      this.offset = this.normaliseOffset(
        this.offset
      );

      this.render();
      this.resumeAfterDelay();
    }

    handleClick(event) {
      if (!this.hasDragged) return;

      event.preventDefault();
      event.stopPropagation();

      this.hasDragged = false;
    }

    handleResize() {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = window.setTimeout(() => {
        this.rebuildLoop();
      }, 150);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pause();
        this.stop();
      } else {
        this.resume();
      }
    }

    handleReducedMotionChange() {
      if (this.reducedMotionQuery.matches) {
        this.pause();
        this.stop();
        this.removeClones();
        this.track.style.transform = '';
      } else {
        this.rebuildLoop();
        this.setInitialOffset();
        this.render();
        this.resume();
      }
    }

    handleSectionSelect(event) {
      if (!event.target.contains(this)) return;

      this.isEditorSelected = true;
      this.pause();
      this.stop();
    }

    handleSectionDeselect(event) {
      if (!event.target.contains(this)) return;

      this.isEditorSelected = false;
      this.resume();
    }

    addEventListeners() {
      if (this.pauseOnHover) {
        this.addEventListener(
          'mouseenter',
          this.pause
        );

        this.addEventListener(
          'mouseleave',
          this.resume
        );
      }

      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);

      this.viewport.addEventListener(
        'pointerdown',
        this.handlePointerDown
      );

      this.viewport.addEventListener(
        'pointermove',
        this.handlePointerMove,
        { passive: false }
      );

      this.viewport.addEventListener(
        'pointerup',
        this.handlePointerUp
      );

      this.viewport.addEventListener(
        'pointercancel',
        this.handlePointerCancel
      );

      this.viewport.addEventListener(
        'lostpointercapture',
        this.handlePointerCancel
      );

      this.viewport.addEventListener(
        'click',
        this.handleClick,
        true
      );

      window.addEventListener(
        'resize',
        this.handleResize
      );

      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      document.addEventListener(
        'shopify:section:select',
        this.handleSectionSelect
      );

      document.addEventListener(
        'shopify:section:deselect',
        this.handleSectionDeselect
      );

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener(
          'change',
          this.handleReducedMotionChange
        );
      } else {
        this.reducedMotionQuery.addListener(
          this.handleReducedMotionChange
        );
      }
    }

    removeEventListeners() {
      this.removeEventListener(
        'mouseenter',
        this.pause
      );

      this.removeEventListener(
        'mouseleave',
        this.resume
      );

      this.removeEventListener(
        'focusin',
        this.pause
      );

      this.removeEventListener(
        'focusout',
        this.resume
      );

      if (this.viewport) {
        this.viewport.removeEventListener(
          'pointerdown',
          this.handlePointerDown
        );

        this.viewport.removeEventListener(
          'pointermove',
          this.handlePointerMove
        );

        this.viewport.removeEventListener(
          'pointerup',
          this.handlePointerUp
        );

        this.viewport.removeEventListener(
          'pointercancel',
          this.handlePointerCancel
        );

        this.viewport.removeEventListener(
          'lostpointercapture',
          this.handlePointerCancel
        );

        this.viewport.removeEventListener(
          'click',
          this.handleClick,
          true
        );
      }

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      document.removeEventListener(
        'shopify:section:select',
        this.handleSectionSelect
      );

      document.removeEventListener(
        'shopify:section:deselect',
        this.handleSectionDeselect
      );

      if (
        this.reducedMotionQuery.removeEventListener
      ) {
        this.reducedMotionQuery.removeEventListener(
          'change',
          this.handleReducedMotionChange
        );
      } else {
        this.reducedMotionQuery.removeListener(
          this.handleReducedMotionChange
        );
      }
    }

    destroy() {
      this.stop();
      this.removeEventListeners();

      if (this.resumeTimer) {
        window.clearTimeout(this.resumeTimer);
      }

      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }

      this.removeClones();

      if (this.track) {
        this.track.style.transform = '';
      }

      if (this.viewport) {
        this.viewport.classList.remove(
          'is-dragging'
        );
      }

      delete this.dataset.ohsInitialised;
    }
  }

  customElements.define(
    ELEMENT_NAME,
    OHSProductCarousel
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      event.target
        .querySelectorAll(ELEMENT_NAME)
        .forEach((carousel) => {
          if (
            carousel.dataset.ohsInitialised !== 'true'
          ) {
            carousel.connectedCallback();
          }
        });
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll(ELEMENT_NAME)
        .forEach((carousel) => {
          if (
            typeof carousel.destroy === 'function'
          ) {
            carousel.destroy();
          }
        });
    }
  );
})();